import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/api';

/**
 * Where traffic actually came from.
 *
 * Two sources, deliberately kept apart rather than summed into one number:
 *
 *   Orders    real sales, attributed to one of our own affiliate links, a
 *             referral, or direct. Meaningful from the first order.
 *   QR        scans, which only exist once codes are printed and used. This
 *             reads zero until then, and says so rather than looking broken.
 *
 * They are not added together because they count different things — a scan is
 * a visit, an order is a sale — and a combined figure would imply a funnel
 * that is not being measured end to end.
 */

interface AttributionSource {
  source: string;
  orders: number;
  revenue: number;
  shareOfOrders: number;
}

interface AttributionReport {
  windowDays: number;
  totalOrders: number;
  totalRevenue: number;
  sources: AttributionSource[];
  recentOrders: Array<{
    confirmationNumber: string | null;
    source: string;
    affiliateName: string | null;
    amountTotal: number;
    currency: string;
    placedAt: string;
  }>;
}

interface ChannelRow {
  _id: string;
  totalClicks: number;
  totalConversions: number;
}

interface QRAnalytics {
  totalCodes: number;
  totalClicks: number;
  totalConversions: number;
  channelPerformance?: ChannelRow[];
}

const WINDOWS = [7, 30, 90];

const SOURCE_LABELS: Record<string, string> = {
  affiliate: 'Affiliate links',
  referral: 'Referrals',
  direct: 'Direct',
};

const SOURCE_NOTES: Record<string, string> = {
  affiliate: 'Arrived on one of our affiliates\u2019 links \u2014 commission owed',
  referral: 'Buyer matches a submitted referral',
  direct: 'No attribution recorded',
};

const money = (minorUnits: number, currency = 'usd') =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format((minorUnits || 0) / 100);

const TrafficAnalytics: React.FC = () => {
  const [days, setDays] = useState(30);
  const [report, setReport] = useState<AttributionReport | null>(null);
  const [qr, setQr] = useState<QRAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Requested together: one is useless without the other for judging
      // where to spend effort, and a failure in either should not blank both.
      const [attribution, qrAnalytics] = await Promise.all([
        apiService.getAttributionReport(days),
        apiService.getQRAnalytics(),
      ]);

      if (attribution.success) {
        setReport((attribution as { report?: AttributionReport }).report ?? null);
      } else {
        setError(attribution.message || 'Could not load the attribution report');
      }

      if (qrAnalytics.success) {
        setQr((qrAnalytics.analytics as QRAnalytics) ?? null);
      }
    } catch (err) {
      console.error('Traffic analytics failed to load:', err);
      setError('Could not reach the server for traffic analytics');
    } finally {
      setIsLoading(false);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex space-x-2">
          {WINDOWS.map((window) => (
            <button
              key={window}
              onClick={() => setDays(window)}
              className={`px-4 py-2 rounded-xl font-bold transition-colors ${
                days === window
                  ? 'bg-brand-blue text-white'
                  : 'bg-white text-deep-navy border border-ice-teal-light hover:border-brand-blue'
              }`}
            >
              {window} days
            </button>
          ))}
        </div>
        <button onClick={load} className="btn-secondary">Refresh</button>
      </div>

      {error && (
        <div className="bg-brand-red/10 border border-brand-red/30 rounded-xl p-4">
          <p className="text-brand-red font-body">{error}</p>
        </div>
      )}

      {isLoading && (
        <div className="bg-ice-pale border border-ice-teal-light rounded-xl p-4 text-center">
          <p className="text-deep-navy/70 font-body">Loading traffic data…</p>
        </div>
      )}

      {/* Orders first: this is the half that has real numbers today. */}
      {report && (
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
          <div className="flex justify-between items-baseline mb-4">
            <h3 className="text-xl font-title font-bold text-deep-navy">Orders by source</h3>
            <p className="text-gray-600 font-body">
              {report.totalOrders} order{report.totalOrders === 1 ? '' : 's'} · {money(report.totalRevenue)} in {report.windowDays} days
            </p>
          </div>

          {report.totalOrders === 0 ? (
            <p className="text-gray-500 font-body">
              No paid orders in this window. Widen the range, or check back after the next sale.
            </p>
          ) : (
            <div className="space-y-3">
              {report.sources.map((source) => (
                <div key={source.source}>
                  <div className="flex justify-between items-baseline mb-1">
                    <span className="font-bold text-deep-navy">
                      {SOURCE_LABELS[source.source] || source.source}
                    </span>
                    <span className="text-deep-navy/70 font-body text-sm">
                      {source.orders} · {money(source.revenue)} · {source.shareOfOrders}%
                    </span>
                  </div>
                  <div className="w-full bg-ice-pale rounded-full h-2">
                    <div
                      className="bg-brand-blue h-2 rounded-full transition-all"
                      style={{ width: `${source.shareOfOrders}%` }}
                    />
                  </div>
                  <p className="text-gray-500 text-xs mt-1">{SOURCE_NOTES[source.source]}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {report && report.recentOrders.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
          <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Recent attributed orders</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ice-teal-light text-left">
                  <th className="py-2 text-deep-navy font-bold">Order</th>
                  <th className="py-2 text-deep-navy font-bold">Source</th>
                  <th className="py-2 text-deep-navy font-bold">Total</th>
                  <th className="py-2 text-deep-navy font-bold">Placed</th>
                </tr>
              </thead>
              <tbody>
                {report.recentOrders.map((order, index) => (
                  <tr key={order.confirmationNumber || index} className="border-b border-ice-teal-light/50">
                    <td className="py-2 font-mono text-sm text-deep-navy">{order.confirmationNumber || '—'}</td>
                    <td className="py-2 text-deep-navy/80">
                      {SOURCE_LABELS[order.source] || order.source}
                      {/* Which affiliate, not just that it was one — the row is
                          only actionable if you can see who earned it. */}
                      {order.affiliateName && (
                        <div className="text-xs text-deep-navy/50">{order.affiliateName}</div>
                      )}
                    </td>
                    <td className="py-2 text-deep-navy/80">{money(order.amountTotal, order.currency)}</td>
                    <td className="py-2 text-deep-navy/60 text-sm">
                      {new Date(order.placedAt).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* QR beneath, and honest about being empty until codes are in use. */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex justify-between items-baseline mb-4">
          <h3 className="text-xl font-title font-bold text-deep-navy">QR performance</h3>
          <p className="text-gray-600 font-body">
            {qr?.totalClicks ?? 0} scan{qr?.totalClicks === 1 ? '' : 's'} · {qr?.totalConversions ?? 0} conversion{qr?.totalConversions === 1 ? '' : 's'}
          </p>
        </div>

        {!qr || qr.totalClicks === 0 ? (
          <p className="text-gray-500 font-body">
            No scans recorded yet. A scan is only counted when someone opens the tracking URL
            from the QR Tracking tab — a code pointing anywhere else will not appear here.
          </p>
        ) : (
          <div className="space-y-2">
            {(qr.channelPerformance || []).map((channel) => (
              <div key={channel._id} className="flex justify-between py-2 border-b border-ice-teal-light/50">
                <span className="text-deep-navy font-body">{channel._id}</span>
                <span className="text-deep-navy/70 font-body text-sm">
                  {channel.totalClicks} scan{channel.totalClicks === 1 ? '' : 's'} · {channel.totalConversions} conversion{channel.totalConversions === 1 ? '' : 's'}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrafficAnalytics;
